import React from 'react';
import { Event } from '../types';
import './EventDetailsDrawer.css';

interface EventDetailsDrawerProps {
  event: Event | null;
  onClose: () => void;
}

export const EventDetailsDrawer: React.FC<EventDetailsDrawerProps> = ({ event, onClose }) => {
  if (!event) return null;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const isTrapped = event.classification === 'trapped';

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="event-drawer">
        <div className="drawer-header">
          <h2>Event Details</h2>
          <button className="drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="drawer-content">
          {/* BASIC INFO */}
          <div className="event-detail-section">
            <h3>Basic Information</h3>

            <div className="detail-row">
              <span className="detail-label">Event ID:</span>
              <span className="detail-value detail-value--mono">{event.id}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Timestamp:</span>
              <span className="detail-value">{formatTimestamp(event.timestamp)}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Source IP:</span>
              <span className="detail-value detail-value--highlight">{event.sourceIp}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Session ID:</span>
              <span className="detail-value detail-value--mono">{event.sessionId}</span>
            </div>
          </div>

          {/* ACTIVITY */}
          <div className="event-detail-section">
            <h3>Activity Details</h3>

            <div className="detail-row">
              <span className="detail-label">Action:</span>
              <span className={`detail-value detail-badge action-${event.action.toLowerCase()}`}>
                {event.action}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Path:</span>
              <span className="detail-value detail-value--mono">{event.path}</span>
            </div>

            {event.method && (
              <div className="detail-row">
                <span className="detail-label">HTTP Method:</span>
                <span className="detail-value detail-badge">{event.method}</span>
              </div>
            )}

            {typeof event.statusCode === 'number' && (
              <div className="detail-row">
                <span className="detail-label">HTTP Status:</span>
                <span className="detail-value detail-badge">
                  {event.statusCode}
                </span>
              </div>
            )}

            {event.severity && (
              <div className="detail-row">
                <span className="detail-label">Severity:</span>
                <span className={`detail-value detail-badge severity-${event.severity}`}>
                  {event.severity.toUpperCase()}
                </span>
              </div>
            )}

            {event.classification && (
              <div className="detail-row">
                <span className="detail-label">Classification:</span>
                <span className={`detail-value detail-badge status-${event.classification}`}>
                  {event.classification.toUpperCase()}
                </span>
              </div>
            )}

            {isTrapped && (
              <div className="detail-row">
                <span className="detail-label">Trap:</span>
                <span className="detail-value detail-badge trap-web">
                  WEB TRAP
                </span>
              </div>
            )}
          </div>

          {/* METADATA */}
          {event.meta && Object.keys(event.meta).length > 0 && (
            <div className="event-detail-section">
              <h3>Metadata</h3>
              {Object.entries(event.meta).map(([key, value]) => (
                <div key={key} className="detail-row">
                  <span className="detail-label">{key}:</span>
                  <span className="detail-value detail-value--wrap">
                    {typeof value === 'object'
                      ? JSON.stringify(value, null, 2)
                      : String(value)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* RAW */}
          <div className="event-detail-section">
            <h3>Raw JSON</h3>
            <pre className="json-viewer">
              {JSON.stringify(event, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </>
  );
};
